<template>
	<div>
		<div v-if="options">
			<div v-if="showGoToPage">
				<form-row label="Goto Page">
					<select class="form-control" v-model="options.gotoPageIndex" v-on:blur="sendUpdate()">
						<option :value="n" v-bind:key="n" v-for="n in possibleGotoIndices">#{{ n + 1 }}</option>
					</select>
				</form-row>
			</div>
		</div>
		<div class="card text-white bg-dark my-3">
			<div class="card-header row mx-0 p-2">
				<div class="col-8">
					{{position}} Component
				</div>
			</div>
			<div class="card-body py-1">
				<form-row label="Type">
					<select class="form-control" v-model="componentIndex">
						<option :value="n" v-bind:key="type" v-for="(type, n) in componentTypes">{{ type }}</option>
					</select>
				</form-row>
				<div class="my-3" style="border-bottom: 2px dashed white"/>
				<div v-if="options">
					<form-row label="Text">
						<markdown-editor v-model="options.text" v-on:input="sendUpdate()"></markdown-editor>
					</form-row>
					<div v-if="component == 'Media'">
						<form-row label="Media">
							<select class="form-control" v-model="selectedMediaType">
								<option value="video">Video Link</option>
								<option value="image">Image</option>
							</select>
						</form-row>
						<div v-if="selectedMediaType == 'video'">
							<form-row label="Link">
								<input class="form-control" type="text" v-bind:class="{'is-invalid': !isMediaLinkValid}"
								       v-model="options.url" v-on:blur="sendUpdate()"/>
							</form-row>
						</div>
						<div v-else>
							<image-picker :showImage="true" :showUploader="false" v-model="options.url"
							              v-on:change="sendUpdate()"/>
						</div>
					</div>
					<div v-else-if="component == 'Choices'">
						<form-row label="Result Type">
							<select class="form-control" v-model="options.resultType" v-on:blur="sendUpdate()">
								<option :value="type" v-bind:key="type" v-for="type in resultTypes">{{ getResultTypeDescription(type) }}</option>
							</select>
						</form-row>
						<div v-if="options.resultType == 'correctAnswer'">
							<form-row label="Correct Index">
								<select class="form-control" v-model="options.correctIndex" v-on:blur="sendUpdate()">
									<option :value="n - 1" v-bind:key="n" v-for="n in options.choices.length">
										{{ n }}
									</option>
								</select>
							</form-row>
						</div>
						<div class="card text-white bg-dark my-3" v-bind:key="i" v-for="(choice, i) in options.choices">
							<div class="card-header row mx-0 p-2">
								<div class="col-8">
									Choice {{i + 1}}
								</div>
								<div class="col d-flex justify-content-end">
									<button class="btn btn-sm btn-danger" v-on:click="deleteChoice(i)">Delete</button>
								</div>
							</div>
							<div class="card-body py-1">
								<form-row 
									label="Goto Page"
									v-if="options.resultType == 'chooseAnswer' || (options.resultType == 'correctAnswer' && options.correctIndex == i)"
								>
									<select
										class="form-control"
										v-model="choice.gotoPageIndex"
									    v-on:blur="sendUpdate()"
									>
										<option :value="n" v-bind:key="n" v-for="n in possibleGotoIndices">
											#{{ n + 1}}
										</option>
									</select>
								</form-row>
								<form-row label="Global Score">
									<input class="form-control" type="number" v-model="choice.globalScore" v-on:blur="sendUpdate()"/>
								</form-row>
								<form-row label="Text">
									<markdown-editor 
										v-model="choice.text"
									    v-on:input="sendUpdate()">
									</markdown-editor>
								</form-row>
							</div>
						</div>
						<div class="d-flex justify-content-center my-3">
							<button :disabled="options.choices.length >= 6" class="btn btn-success btn-sm"
							        v-on:click="addChoice()">Add New Choice
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import Vue from 'vue';
	import FormRow from '@/components/form-row';
	import MediaManager from '@/media-manager';
	import ImagePicker from '@/components/image-picker';
	import MarkdownEditor from '@/components/markdown-editor';

	export default {
		name: 'component-editor',
		props: {
			content: {
				type: Object
			},
			position: {
				type: String
			},
			pagesInCase: {
				type: Number
			},
			pageIndex: {
				type: Number
			},
			pageType: {
				type: String
			}
		},
		components: {
			FormRow,
			ImagePicker,
			MarkdownEditor
		},
		data() {
			return {
				componentIndex: 0,
				options: null,
				selectedMediaType: 'image'
			}
		},
		mounted() {
			if (this.content) {
				this.componentIndex = this.componentTypes.indexOf(this.content.component);
				this.options = this.content.options;

				if (this.component == 'Media') {
					this.selectedMediaType = this.isMediaLinkValid ? 'video' : 'image';
				}
			} else {
				this.setDefaultOptions();
				this.sendUpdate();
			}

			this.$watch('componentIndex', () => {
				this.setDefaultOptions();
				this.sendUpdate();
			});
		},
		methods: {
			sendUpdate() {
				this.sanitizeOptions();

				this.$emit('updated', {
					component: this.component,
					options: this.options
				});
			},
			sanitizeOptions() {
				if (this.component === 'Choices') {
					if (this.options.resultType !== 'correctAnswer') {
						if (this.options.hasOwnProperty('correctIndex')) {
							Vue.delete(this.options, 'correctIndex');
						}
					}

					if (this.options.resultType !== 'chooseAnswer' && this.options.hasOwnProperty('choices') && this.options.choices) {
						for (let i = 0, len = this.options.choices.length; i < len; i++) {
							if (this.options.choices[i].hasOwnProperty('gotoPageIndex')) {
								Vue.delete(this.options.choices[i], 'gotoPageIndex');
							}
						}
					}

					// For some reason it can be set as a string
					for (const choice of this.options.choices) {
						if (typeof choice.globalScore !== 'number') {
							choice.globalScore = parseInt(choice.globalScore);
						}
					}
				}


				if (this.component === 'Choices' && this.options.resultType === 'noAnswer' && this.options.hasOwnProperty(
					'gotoPageIndex')) {
					Vue.delete(this.options, 'gotoPageIndex');
				}

				console.log('Doing a sanitize!!!',this.component, this.options);
			},
			clearOptions() {
				for (let prop in this.options) {
					if (this.options.hasOwnProperty(prop)) {
						Vue.delete(this.options, prop);
					}
				}
			},
			setDefaultOptions() {
				this.clearOptions();

				this.options = Object.assign({}, this.options, this.defaultOptions);

				switch (this.component) {
					case 'Media':
						this.options = Object.assign({}, this.options, this.defaultMediaOptions);
						break;
					case 'Choices':
						this.options = Object.assign({}, this.options, this.defaultChoicesOptions);
						break;
				}
			},
			addChoice() {
				this.options.choices.push(this.createNewChoice());
				this.sendUpdate();
			},
			deleteChoice(index) {
				this.options.choices.splice(index, 1);
				this.sendUpdate();
			},
			createNewChoice() {
				return {
					gotoPageIndex: this.pageIndex + 1,
					globalScore: 0,
					text: ''
				}
			},
			getResultTypeDescription(type) {
				switch (type) {
					case 'noAnswer':
						return 'Popularity vote';
					case 'correctAnswer':
						return 'One answer is correct';
					case 'chooseAnswer':
						return 'Facilitator chooses a correct answer';
					default:
						return 'Unknown result type';
				}
			}
		},
		computed: {
			component() {
				return this.componentTypes[this.componentIndex];
			},
			componentTypes() {
				let types = [];
				if (this.pageType == 'dilemma') {
					types = ['Text', 'Media'];
					if (!this.isLastPage) {
						types.push('Choices');
					}
				}
				
				return types;
			},
			resultTypes() {
				return ['noAnswer', 'correctAnswer', 'chooseAnswer'];
			},
			showGoToPage() {
				return this.pageType == 'dilemma' && !this.isLastPage && this.position == 'Left' && (this.component != 'Choices' || this.options.resultType == 'noAnswer');
			},
			possibleGotoIndices() {
				let indices = [];
				for (let i = 0; i < this.pagesInCase; i++) {
					if (i != this.pageIndex) {
						indices.push(i);
					}
				}
				return indices;
			},
			defaultOptions() {
				return {
					text: '',
					gotoPageIndex: this.pageIndex + 1
				}
			},
			defaultMediaOptions() {
				return {
					url: ''
				}
			},
			defaultChoicesOptions() {
				return {
					choices: [this.createNewChoice()],
					resultType: 'noAnswer'
				}
			},
			isMediaLinkValid() {
				return MediaManager.urlIsVimeo(this.options.url) || MediaManager.urlIsYoutube(this.options.url);
			},
			isLastPage() {
				return this.pageIndex == this.pagesInCase - 1;
			}
		}
	}
</script>

<style lang="less" scoped>
	.invalid-feedback {
		font-size: 14px;
	}
</style>
